import { trimLeadingNewlines } from './helpers/api-helpers'
import { CommentType } from '@/types/pdf-viewer'
import type {
  Comment,
  CommentsResponse,
  MeasurementExportData,
} from '@/types/pdf-viewer'
import type {
  GetCommentsApiResponse,
  GetMeasurementsApiResponse,
  GetKeynotesMatchingApiResponse,
  ApiKeynoteBox,
  ApiComment,
  BackendComment,
  BackendKeynotesMatchingResponseV2,
  BackendKeynotesMatchingRecordV2,
  BackendKeynoteMatchItemV2,
  KeynoteBox,
  KeynotesMatchingResponse,
} from '../types/playground-api-types'

function toCommentType(raw?: string): CommentType {
  switch (raw) {
    case 'AIComment':
      return CommentType.AIComment
    case 'QpointCoreComment':
      return CommentType.QpointCoreComment
    case 'CityClerkComment':
      return CommentType.CityClerkComment
    default:
      return CommentType.CityClerkComment
  }
}

export function normalizeCommentsResponse(
  raw: GetCommentsApiResponse,
  fallbackDocumentId: string,
): CommentsResponse {
  let containerDocumentId: string | undefined
  let apiComments: ApiComment[] = []

  if (Array.isArray(raw)) {
    apiComments = raw.map((comment) => ({
      id: comment.id,
      text: comment.text,
      createdAt: comment.createdDate,
      documentId: comment.sitePlanEntityId,
      sitePlanEntityId: comment.sitePlanEntityId,
      xNorm: comment.coordinates?.x,
      yNorm: comment.coordinates?.y,
      replyCount: comment.replyCount,
      creaetorFullName: comment.creatorFullName,
      isAiComment:
        comment.isAiComment ??
        (comment.commentType === 'AIComment' ||
          comment.commentType === 'QpointCoreComment'),
      commentType: toCommentType(comment.commentType),
      replyTargetId: comment.id,
    }))
  } else if ('comments' in raw) {
    containerDocumentId = raw.documentId
    apiComments = (raw.comments ?? []) as ApiComment[]
  } else {
    const backend = raw as BackendComment
    apiComments = [
      {
        id: backend.id,
        text: backend.text,
        createdAt: backend.createdDate,
        documentId: backend.sitePlanEntityId,
        sitePlanEntityId: backend.sitePlanEntityId,
        xNorm: backend.coordinates?.x,
        yNorm: backend.coordinates?.y,
        replyCount: backend.replyCount,
        creaetorFullName: backend.creatorFullName,
        isAiComment: backend.isAiComment,
        commentType: toCommentType(backend.commentType),
        replyTargetId: backend.id,
      },
    ]
  }

  const documentId = containerDocumentId ?? fallbackDocumentId

  const comments: Comment[] = apiComments.map((comment, index) => {
    const status = comment.status ?? (comment.resolved ? 'resolved' : 'open')
    return {
      id: comment.id ?? `comment-${index}`,
      documentId: comment.documentId ?? documentId,
      pageIndex: comment.pageIndex ?? 0,
      xNorm: comment.xNorm ?? 0.5,
      yNorm: comment.yNorm ?? 0.5,
      status: comment.status ?? 'open',
      creaetorFullName: comment.creaetorFullName ?? 'Reviewer',
      text: trimLeadingNewlines(comment.text ?? comment.content ?? ''),
      createdAt: comment.createdAt ?? new Date().toISOString(),
      box: comment.box,
      boxConnectorOffset: comment.boxConnectorOffset ?? 0,
      pinAngle: comment.pinAngle ?? 0,
      replies: comment.replies ?? [],
      isAiComment: comment.isAiComment ?? false,
      commentType: comment.commentType ?? CommentType.CityClerkComment,
      replyTargetId: comment.replyTargetId ?? comment.id ?? `comment-${index}`,
      position: comment.position,
      content: comment.content,
      resolved: comment.resolved ?? status === 'resolved',
      markerType: comment.markerType ?? 'round',
      replyCount: comment.replyCount ?? 0,
      sitePlanEntityId: comment.sitePlanEntityId,
    }
  })
  return { documentId, comments }
}

export function normalizeMeasurementExportData(
  raw: GetMeasurementsApiResponse | null,
  fallbackDocumentId: string,
): MeasurementExportData | null {
  if (!raw) return null
  const documentId = raw.documentId ?? fallbackDocumentId
  return {
    version: 1,
    documentId,
    scale: raw.scale,
    lines: raw.lines ?? [],
    polygons: raw.polygons ?? [],
    circles: raw.circles ?? [],
  }
}

function normalizeKeynoteBox(raw: ApiKeynoteBox, index: number): KeynoteBox {
  return {
    id: raw.id ?? `keynote-${index}`,
    keynoteLabel: raw.keynote_label ?? '',
    keynoteGroupId: raw.keynote_group_id ?? raw.keynote_label ?? '',
    pageIndex: raw.page_index ?? 0,
    layerId: raw.layer_id ?? 'keynotes',
    layerName: raw.layer_name ?? 'Keynotes',
    layerColor: raw.layer_color ?? '#111827',
    name: raw.name ?? raw.text ?? raw.keynote_label ?? 'Keynote',
    visible: raw.visible ?? true,
    x: raw.x ?? 0,
    y: raw.y ?? 0,
    width: raw.width ?? 0.02,
    height: raw.height ?? 0.02,
    score: raw.score ?? 1,
    text: raw.text ?? null,
    style: {
      fill: raw.style?.fill ?? '#FFFFFF',
      stroke: raw.style?.stroke ?? '#111827',
      strokeWidth: raw.style?.stroke_width ?? 1.5,
      opacity: raw.style?.opacity ?? 1,
    },
  }
}

export function normalizeKeynotesMatchingResponse(
  raw: GetKeynotesMatchingApiResponse,
  fallbackAttachmentId: string,
): KeynotesMatchingResponse {
  const attachmentId = raw.attachment_id ?? fallbackAttachmentId
  const wrap = (items: ApiKeynoteBox[]) =>
    items.map((item, index) => normalizeKeynoteBox(item, index))

  return {
    attachmentId,
    referenceKeynotes: wrap(raw.response?.reference_keynotes ?? []),
    planKeynotes: wrap(raw.response?.plan_keynotes ?? []),
    referenceKeynotesMissingInPlan: wrap(
      raw.response?.reference_keynotes_missing_in_plan ?? [],
    ),
    planKeynotesMissingInReference: wrap(
      raw.response?.plan_keynotes_missing_in_reference ?? [],
    ),
  }
}

export function backendKeynotesV2ToApiResponse(
  raw: BackendKeynotesMatchingResponseV2 | unknown,
  requestedAttachmentId: string,
): GetKeynotesMatchingApiResponse {
  const unwrapRecords = (
    value: BackendKeynotesMatchingResponseV2 | unknown,
  ): BackendKeynotesMatchingRecordV2[] => {
    if (!value) return []
    if (Array.isArray(value)) return value as BackendKeynotesMatchingRecordV2[]
    if (
      typeof value === 'object' &&
      value !== null &&
      'data' in (value as { data?: unknown })
    ) {
      const data = (value as { data?: unknown }).data
      if (Array.isArray(data)) return data as BackendKeynotesMatchingRecordV2[]
    }
    return []
  }

  const records = unwrapRecords(raw)
  const emptyResponse: GetKeynotesMatchingApiResponse = {
    attachment_id: requestedAttachmentId,
    response: {
      reference_keynotes: [],
      plan_keynotes: [],
      reference_keynotes_missing_in_plan: [],
      plan_keynotes_missing_in_reference: [],
    },
  }

  if (records.length === 0) return emptyResponse

  const recordForAttachment =
    records.find(
      (r) =>
        r.attachmentId &&
        requestedAttachmentId &&
        r.attachmentId.toLowerCase() === requestedAttachmentId.toLowerCase(),
    ) ?? records[0]

  const toApiBox = (
    item: BackendKeynoteMatchItemV2,
    kind: 'reference' | 'plan' | 'reference-missing' | 'plan-missing',
  ): ApiKeynoteBox => {
    const groupId =
      item.aiReferenceId != null ? String(item.aiReferenceId) : (item.id ?? '')
    const isPlanKind = kind === 'plan' || kind === 'plan-missing'
    const layerId = isPlanKind ? 'keynotes-plan' : 'keynotes-reference'
    const layerName = isPlanKind ? 'Keynotes Locations' : 'Keynotes Legend'
    const layerColor = isPlanKind ? '#10B981' : '#111827'

    return {
      id: item.id ?? `${kind}-${groupId}-${item.x}-${item.y}`,
      keynote_label: groupId,
      keynote_group_id: groupId,
      page_index: 0,
      layer_id: layerId,
      layer_name: layerName,
      layer_color: layerColor,
      name:
        (item.label && item.label.trim()) || `Keynote ${groupId || ''}`.trim(),
      visible: true,
      type: 'rectangle',
      x: item.x ?? 0,
      y: item.y ?? 0,
      width: item.width ?? 0.02,
      height: item.height ?? 0.02,
      score: item.score ?? 1,
      text: null,
      style: {},
    }
  }

  const hasValidPosition = (item: BackendKeynoteMatchItemV2) =>
    (item.x != null && item.x !== 0) || (item.y != null && item.y !== 0)

  return {
    attachment_id: recordForAttachment.attachmentId ?? requestedAttachmentId,
    response: {
      reference_keynotes:
        recordForAttachment.referenceKeynotes
          ?.filter(hasValidPosition)
          .map((item) => toApiBox(item, 'reference')) ?? [],
      plan_keynotes:
        recordForAttachment.planKeynotes
          ?.filter(hasValidPosition)
          .map((item) => toApiBox(item, 'plan')) ?? [],
      reference_keynotes_missing_in_plan:
        recordForAttachment.referenceKeynotesMissingInPlan
          ?.filter(hasValidPosition)
          .map((item) => toApiBox(item, 'reference-missing')) ?? [],
      plan_keynotes_missing_in_reference:
        recordForAttachment.planKeynotesMissingInReference
          ?.filter(hasValidPosition)
          .map((item) => toApiBox(item, 'plan-missing')) ?? [],
    },
  }
}
